import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useApolloClient } from "@apollo/client/react";

import { RepoCommitFileAction } from "types/generated";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useCopyToClipboard from "hooks/useCopyToClipboard";
import { GET_SPACELIFT_VCS_FILES } from "shared/SpaceliftVcsRepo/useSearchSpaceliftVcsFiles/gql";

import { getFileEditUrl } from "../helpers";
import SpaceliftVcsRepoCommitDialog, { CommitArgs } from "../CommitDialog";
import useRepoCommit from "../useRepoCommit";

type SpaceliftVcsRepoFileRowActionsProps = {
  filePath: string;
  onDeleteSuccess?: () => void;
};

const SpaceliftVcsRepoFileRowActions = ({
  filePath,
  onDeleteSuccess,
}: SpaceliftVcsRepoFileRowActionsProps) => {
  const { repoId } = useParams<{ repoId: string }>();
  const navigate = useNavigate();
  const client = useApolloClient();
  const { commit, loading: commitLoading } = useRepoCommit();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const editUrl = getFileEditUrl(repoId!, filePath);
  const copyUrl = `${window.location.origin}${editUrl}`;
  const handleCopyLink = useCopyToClipboard(copyUrl);

  const handleDelete = ({
    filePath: deletePath = "",
    commitMessage,
    commitDescription,
  }: CommitArgs) => {
    commit(
      [
        {
          path: deletePath,
          content: null,
          encrypt: null,
          fileMode: null,
          action: RepoCommitFileAction.Delete,
        },
      ],
      commitMessage,
      commitDescription,
      () => {
        setIsDeleteDialogOpen(false);
        onDeleteSuccess?.();
        client.refetchQueries({ include: [GET_SPACELIFT_VCS_FILES] });
      }
    );
  };

  return (
    <>
      <DropdownMenuEllipsis tooltip="Row actions">
        <DropdownMenuItem onAction={() => navigate(editUrl)}>Edit</DropdownMenuItem>
        <DropdownMenuItem onAction={handleCopyLink}>Copy link to file</DropdownMenuItem>
        <DropdownMenuItem danger onAction={() => setIsDeleteDialogOpen(true)}>
          Delete
        </DropdownMenuItem>
      </DropdownMenuEllipsis>

      <SpaceliftVcsRepoCommitDialog
        variant="delete"
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onCommit={handleDelete}
        loading={commitLoading}
        filePath={filePath}
      />
    </>
  );
};

export default SpaceliftVcsRepoFileRowActions;
